import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { ClockIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { dateToString } from "../../Utils";
import { resolveOfferActionErrorMessage } from "./utils";

/**
 * Generic version-history dialog — reused for both calculation-template
 * versions (`/offer_calculations/:id/versions`) and offer-template versions
 * (`/offers/:id/template_versions`). Callers pass the fetch/restore
 * functions bound to the right entity so this component owns only the
 * list/loading/confirm UI (no duplicated dialog chrome per module).
 *
 * `fetchVersions(entityId)` → `{ data: [{ id, version_number, created_by, change_note, created_at }] }`
 * `restoreVersion(entityId, versionId)` → soft-error envelope on failure.
 */
export default function VersionHistoryDialog({
  open,
  onClose,
  title,
  entityId,
  fetchVersions,
  restoreVersion,
  onRestored,
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pendingRestoreId, setPendingRestoreId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (!open || !entityId) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setMissing(false);
    setPendingRestoreId(null);
    (async () => {
      const result = await fetchVersions(entityId);
      if (cancelled) return;
      if (result?.missing) {
        setMissing(true);
        setVersions([]);
      } else if (result?.error) {
        setLoadError(
          resolveOfferActionErrorMessage(t, result, "offers.versions.load_failed")
        );
        setVersions([]);
      } else {
        setVersions(Array.isArray(result?.data) ? result.data : []);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entityId, fetchVersions, t]);

  const handleClose = () => {
    if (restoringId) return;
    setPendingRestoreId(null);
    onClose?.();
  };

  const handleRestore = async (versionId) => {
    if (!entityId || restoringId) return;
    setRestoringId(versionId);
    try {
      const result = await restoreVersion(entityId, versionId);
      if (result?.error || result?.status >= 400) {
        toast.error(
          resolveOfferActionErrorMessage(t, result, "offers.versions.restore_failed")
        );
        return;
      }
      toast.success(t("offers.versions.restore_success"));
      setPendingRestoreId(null);
      await onRestored?.(result);
      onClose?.();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.versions.restore_failed")
      );
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={handleClose}
      title={title || "offers.versions.title"}
      size="lg"
      closeOnOutsideClick={!restoringId}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center py-10">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-[#1463C7]" />
          </div>
        ) : missing ? (
          <p className="py-8 text-center text-sm text-slate-500">
            {t("offers.versions.unavailable")}
          </p>
        ) : loadError ? (
          <p className="py-8 text-center text-sm text-red-600">{loadError}</p>
        ) : versions.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
            <ClockIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
            <p className="text-sm font-medium text-slate-700">
              {t("offers.versions.empty")}
            </p>
            <p className="max-w-sm text-xs text-slate-500">
              {t("offers.versions.empty_hint")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {versions.map((version) => {
              const isPending = pendingRestoreId === version.id;
              const isRestoringThis = restoringId === version.id;
              return (
                <li key={version.id} className="flex items-start justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {t("offers.versions.version_number", {
                          number: version.version_number ?? version.id,
                        })}
                      </span>
                      {version.created_at ? (
                        <span className="text-xs text-slate-400">
                          {dateToString(version.created_at)}
                        </span>
                      ) : null}
                    </div>
                    {version.created_by ? (
                      <p className="mt-0.5 text-xs text-slate-500">
                        {t("offers.versions.by", {
                          name:
                            version.created_by?.name ||
                            version.created_by?.email ||
                            String(version.created_by),
                        })}
                      </p>
                    ) : null}
                    {version.change_note ? (
                      <p className="mt-1 text-sm text-slate-600">{version.change_note}</p>
                    ) : null}
                  </div>
                  <div className="flex-shrink-0">
                    {isPending ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPendingRestoreId(null)}
                          disabled={isRestoringThis}
                          className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          {t("Cancel")}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRestore(version.id)}
                          disabled={isRestoringThis}
                          className="rounded-lg border border-transparent bg-red-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isRestoringThis
                            ? t("Loading...")
                            : t("offers.versions.confirm_restore")}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingRestoreId(version.id)}
                        disabled={Boolean(restoringId)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("offers.versions.restore")}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={Boolean(restoringId)}
            className="inline-flex justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            {t("Close")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
